import React from 'react';
import { PropertyWithDetails } from './types';
import { useCategories } from '../category';
import { 
  PagerBar, 
  SearchListBar,
  SearchCriterion,
  SearchableField,
  SortBar,
  SortOption,
  SortColumn,
  ListItemSection,
  LoadingBar,
  formatCategoryName,
  formatAddress,
  formatContacts,
  ListItemDisplayData
} from '../../../components/common';
import '../../../components/common/CRMList.css';

interface PropertyListProps {
  properties: PropertyWithDetails[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  selectedPropertyId?: number;
  onSelectProperty: (property: PropertyWithDetails) => void;
  onDeleteProperty: (property: PropertyWithDetails) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onCreateNew: () => void;
  onSearch?: (searchParams: any) => void;
  globalLoading: boolean;
  loadingMessage: string;
}

const PropertyList: React.FC<PropertyListProps> = ({
  properties,
  loading,
  error,
  pagination,
  selectedPropertyId,
  onSelectProperty,
  onDeleteProperty,
  onPageChange,
  onLimitChange,
  onCreateNew,
  onSearch,
  globalLoading,
  loadingMessage
}) => {
  const { categories } = useCategories();
  
  // Search state for search and sort functionality
  const [searchValue, setSearchValue] = React.useState('');
  const [selectedCriteria, setSelectedCriteria] = React.useState<SearchCriterion[]>([]);
  const [currentSorts, setCurrentSorts] = React.useState<SortOption[]>([
    {
      field: 'name',
      direction: 'asc',
      label: 'Property Name'
    }
  ]);
  
  // Searchable fields configuration for SearchListBar
  // Property Type: primary PropertiesAddresses.categoryId → Categories.name
  // Name: Properties.name
  // Address: any PropertiesAddresses.addressId → Addresses.street1
  // Tenant Name: PropertiesContacts.propertyId references Properties.id and PropertiesContacts.contactId references Contacts.id filter on Contacts.firstName and Contacts.lastName
  const searchableFields: SearchableField[] = [
    { key: 'categoryName', label: 'Property Type', type: 'text' },
    { key: 'name', label: 'Name', type: 'text' },
    { key: 'street', label: 'Address', type: 'text' },
    { key: 'tenantName', label: 'Tenant Name', type: 'text' }
  ];
  
  // Sort column configuration - must match server valid sort fields
  const sortColumns: SortColumn[] = [
    { field: 'name', label: 'Property Name' },
    { field: 'description', label: 'Description' },
    { field: 'dateCreated', label: 'Date Created' },
    { field: 'dateUpdated', label: 'Date Updated' }
  ];
  
  // Handle search value changes (debounced)
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (onSearch) {
        if (selectedCriteria.length > 0) {
          // Multiple field search (OR logic)
          onSearch({
            search: searchValue,
            fields: selectedCriteria.map(c => c.field),
            page: 1 // Reset to first page on search
          });
        } else {
          // Global search across all fields
          onSearch({
            search: searchValue,
            fields: undefined,
            page: 1 // Reset to first page on search
          });
        }
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchValue, selectedCriteria]);
  
  // Handle criteria changes - only update state, let useEffect handle search
  const handleCriteriaChange = React.useCallback((criteria: SearchCriterion[]) => {
    setSelectedCriteria(criteria);
  }, []);
  
  // Handle sort changes
  const handleSortsChange = React.useCallback((sorts: SortOption[]) => {
    setCurrentSorts(sorts);
    
    if (onSearch) {
      if (sorts.length > 0) {
        // Convert sorts to the new format
        const sortArray = sorts.map(sort => ({
          field: sort.field as 'name' | 'description' | 'dateCreated' | 'dateUpdated',
          direction: sort.direction as 'asc' | 'desc'
        }));
        
        onSearch({
          sorts: sortArray,
          page: 1 // Reset to first page when sorting changes
        });
      } else {
        // Clear sorts if none selected
        onSearch({
          sorts: undefined,
          page: 1
        });
      }
    }
  }, [onSearch]);
  
  // Unified display data function for PropertyList
  const getDisplayData = (property: PropertyWithDetails): ListItemDisplayData => ({
    title: property.name,
    category: formatCategoryName(property.categoryId, categories),
    fields: [
      {
        name: 'address',
        value: formatAddress(property.address)
      },
      {
        name: 'contact',
        value: formatContacts(property.contacts)
      }
    ]
  });

  if (error) {
    return <div className="app-error-message">{error}</div>;
  }

  return (
    <div className="list">
      <div className="list-header">
        <h3>Properties ({pagination.total})</h3>
        <button
          className="app-btn app-btn-primary list-btn-add-item"
          onClick={onCreateNew}
          title="Add New Property"
        >
          +
        </button>
      </div>
      
      {/* Search Bar */}
      <SearchListBar
        searchableFields={searchableFields}
        searchValue={searchValue}
        selectedCriteria={selectedCriteria}
        onSearchChange={setSearchValue}
        onCriteriaChange={handleCriteriaChange}
        placeholder="Search properties..."
      />
      
      {/* Sort Bar */}
      <SortBar
        availableColumns={sortColumns}
        currentSorts={currentSorts}
        onSortsChange={handleSortsChange}
        defaultSort={{
          field: 'name',
          direction: 'asc',
          label: 'Property Name'
        }}
      />

      <ListItemSection
        items={properties}
        selectedItemId={selectedPropertyId}
        onSelectItem={onSelectProperty}
        onDeleteItem={onDeleteProperty}
        deleteConfirmMessage={(property) => 
          `Are you sure you want to delete ${property.name}? This action cannot be undone.`
        }
        loading={false}
        loadingMessage=""
        emptyMessage="No properties found. Try adjusting your search criteria."
        getDisplayData={getDisplayData}
      />

          {/* Bottom PagerBar */}
          <PagerBar
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            itemsPerPage={pagination.limit}
            onPageChange={onPageChange}
            onItemsPerPageChange={onLimitChange}
          />
          
          <LoadingBar isLoading={globalLoading} message={loadingMessage} />
    </div>
  );
};

export default PropertyList;
